import {
  AnimationGroup,
  BaseTexture,
  Bone,
  Color3,
  Color4,
  Engine,
  Material,
  Scene,
  TransformNode,
  Vector2,
  Vector3,
} from '@babylonjs/core';
import { Asset } from './Asset';
import { AssetDefinitionQueryResult } from './AssetFactory';
import { AssetObject, DistributionConfig } from './AssetObject';
import { DecorationConfig } from './DecorationGenerator';
import { DungeonConfig } from './DungeonGenerator';
import { EnemyConfig } from './EnemyGenerator';
import { FogConfig } from './FogConfig';
import { LightConfig } from './LightConfig';
import { GroundConfig, HeightMapConfig, SkyBoxConfig } from './MeshFactory';
import { ObstacleConfig } from './ObstacleGenerator';
import { PatternTypeConfig } from './PathFactory';
import { TextureAtlas } from './TextureAtlas';

export enum PathState {
  EMPTY = 'EMPTY',
  GAP = 'GAP',
  COLLECTIBLE = 'COLLECTIBLE',
  ENEMY = 'ENEMY',
  OBSTACLE = 'OBSTACLE',
  CHECKPOINT = 'CHECKPOINT',
}

export interface MarkovConfig {
  /**
   * The transition matrix for the Markov chain.
   * Example: transitionMatrix['EMPTY']['ENEMY'] = 0.1 (10% chance for an enemy after an empty segment)
   */
  transitionMatrix: Partial<Record<PathState, Partial<Record<PathState, number>>>>;

  /**
   * Initial state for the very first point
   */
  initialState: PathState;
}

export class LevelConfig {
  constructor(
    /**
     * Name of the level
     */
    public name: string,

    /**
     * Seed for random number generator
     */
    public seed: number,

    /**
     * Time limit for the level
     */
    public timeLimit: number,

    /**
     * Gravity for the level
     */
    public gravity: Vector3,

    /**
     * Killing height - player will die if he falls below this y position
     */
    public killingHeight: number,

    /**
     * Height above ground
     * Levels dimensions are considered using this height above ground
     */
    public heightAboveGround: number,

    /**
     * Checkpoint interval
     */
    public checkpointInterval: number,

    /**
     * Goal end offset
     */
    public goalEndOffset: number,

    /**
     * Player configuration
     */
    public player: PlayerConfig,

    /**
     * Camera configuration
     */
    public camera: CameraConfig,

    /**
     * Path config
     */
    public path: PathConfig,

    /**
     * Light configuration
     */
    public light: LightConfig,

    /**
     * Skybox configuration
     */
    public skybox: SkyBoxConfig,

    /**
     * Material configuration
     */
    public material: MaterialConfig,

    /**
     * Optional fog configuration
     */
    public fog?: FogConfig,

    /**
     * Optional ground configuration
     */
    public ground?: GroundConfig,

    /**
     * Optional height map ground configuration
     */
    public heightMap?: HeightMapConfig,

    /**
     * Optional ceiling configuration
     */
    public ceiling?: HeightMapConfig,

    /**
     * Optional way configuration, if present the player will mainly move on ways
     */
    public way?: WayGenerationConfig,

    /**
     * Optional platform configuration, if present the player will mainly move on platforms
     */
    public platform?: PlatformGenerationConfig,

    /**
     * Optional collectible configuration, if present collectibles will be generated along the path
     */
    public collectible?: CollectibleDistributionConfig,

    /**
     * Optional base scene where the level is built around
     */
    public baseScene?: BaseSceneConfig,

    /**
     * Optional decoration configuration, if present decoration will be generated along the path
     */
    public decoration?: DecorationConfig,

    /**
     * Optional enemy configuration, if present enemies will be spawned along the path
     */
    public enemy?: EnemyConfig,

    /**
     * Optional obstacle configuration, if present obstacles will be spawned along the path
     */
    public obstacle?: ObstacleConfig,

    /**
     * Optional dungeon configuration, if present a dungeon will be generated
     */
    public dungeon?: DungeonConfig,

    /**
     * Optional water, if present a water plane will be generated at this level
     */
    public water?: WaterConfig,

    /**
     * Optional sound configuration
     */
    public sound?: SoundConfig,

    /**
     * Optional post process configuration
     */
    public postProcess?: PostProcessConfig,

    /**
     * Optional path lights configuration
     */
    public pathLights?: PathLightsConfig,

    /**
     * Optional grass configuration
     */
    public grass?: GrassConfig,

    /**
     * Optional Markov chain configuration for procedural generation
     * If configured, obstacles, enemies and collectibles will be generated based on the transition matrix.
     * The transition matrix is a 2D array where the rows are the current state and the columns are the next state.
     * The values in the matrix are the probabilities of transitioning from the current state to the next state.
     * The other probability mechanisms are ignored.
     */
    public markov?: MarkovConfig,

    public assets: Asset[] = [],
    public sprites: SpriteConfig[] = [],
    public atlasses: TextureAtlas[] = []
  ) {}

  toJson(): string {
    const ignoreProps = ['usedMeshes', 'task', 'pathPoints', 'data', 'scene', 'baseTexture', 'mormalMapTexture', 'frames'];
    return JSON.stringify(
      this,
      (key, value) => {
        if (ignoreProps.includes(key)) {
          // pathPoints is used both as a config number (PathConfig.pathPoints)
          // and as a generated Vector3[] array (Level.pathPoints, HeightMapConfig.pathPoints).
          // Only skip the generated array, not the config number.
          if (key === 'pathPoints' && !Array.isArray(value)) {
            return value;
          }
          return undefined;
        }

        if (key === 'shape' && Array.isArray(value)) {
          return {
            points: value,
            solidHalfCount: (value as any).solidHalfCount,
            closedShape: (value as any).closedShape,
          };
        }

        if (value instanceof Map) {
          return { _isMap: true, entries: Array.from(value.entries()) };
        }

        if (value instanceof Vector3) {
          return { x: value.x, y: value.y, z: value.z };
        }
        if (value instanceof Vector2) {
          return { x: value.x, y: value.y };
        }
        if (value instanceof Color3) {
          return { r: value.r, g: value.g, b: value.b };
        }
        if (value instanceof Color4) {
          return { r: value.r, g: value.g, b: value.b, a: value.a };
        }

        if (
          value instanceof BaseTexture ||
          value instanceof Scene ||
          value instanceof Engine ||
          value instanceof Material ||
          value instanceof TransformNode ||
          value instanceof Bone ||
          value instanceof AnimationGroup ||
          value instanceof HTMLImageElement
        ) {
          return undefined;
        }

        return value;
      },
      2
    );
  }

  fromJson(json: string): void {
    Object.assign(this, JSON.parse(json));
  }

  static fromJson(json: string): LevelConfig {
    const config = new LevelConfig(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined
    );
    config.fromJson(json);
    return config;
  }
}

/**
 * Configures the grass generator
 */
export interface GrassConfig {
  /**
   * Blade height
   */
  bladeHeight: number;

  /**
   * Blade width at base
   */
  bladeWidth: number;

  /**
   * Blades per path point
   */
  density: number;

  /**
   * Path point interval for placement
   */
  interval: number;

  /**
   * Lateral zone for placement
   */
  zone: NumberRange;

  /**
   * Keep-out zone around path
   */
  deadZone?: NumberRange;

  /**
   * Wind animation speed
   */
  windSpeed: number;

  /**
   * Max displacement amount
   */
  windStrength: number;

  /**
   * Base color (bottom)
   */
  baseColor: Color3;

  /**
   * Tip color (top)
   */
  tipColor: Color3;

  /**
   * Random scale range
   */
  scale: NumberRange;

  /**
   * Whether the grass should cast shadows
   */
  castShadows?: boolean;

  /**
   * Whether the grass should receive shadows
   */
  receiveShadows?: boolean;

  /**
   * Frustum culling is activated if depth > 0
   */
  frustumDepth?: number;
}

/**
 * Configures a sound for entities.
 * All entities use spatial sound.
 */
export interface EntitySoundConfig {
  /**
   * Name of the sound
   */
  name: string;

  /**
   * Name of the file, if present the sound will be created
   */
  fileName?: string;

  /**
   * If the sound should play after loading
   */
  autoplay: boolean;
}

/**
 * Configuration for procedural collectible generation along a path
 */
export interface CollectibleDistributionConfig extends DistributionConfig {
  /**
   * Spawn range
   */
  spawnRange: number;
  /**
   * Despawn range
   */
  despawnRange: number;
  /**
   * Maximum pattern size
   */
  maxPatternSize: number;

  /**
   * Whether to mix different collectible types within patterns
   */
  mixPatternTypes: boolean;

  /**
   * Offset from path for collectibles
   */
  offset?: Vector3;
}

export class PlatformAssetConfig implements AssetDefinitionQueryResult {
  constructor(
    public assets: Asset[],
    public assetObjects: AssetObject[],
    public scale: number
  ) {}

  static from(result: AssetDefinitionQueryResult, scale: number = 1): PlatformAssetConfig {
    return new PlatformAssetConfig(result.assets, result.assetObjects, scale);
  }
}

/**
 * Configuration for procedural platform generation along a path
 */
export interface PlatformGenerationConfig {
  // ========== Platform Rules ==========

  /**
   * Number of path points between each platform
   * Lower value = more platforms
   */
  platformInterval: number;

  /**
   * Random height offset from the path point
   */
  heightVariance: { min: number; max: number };

  /**
   * Random lateral offset perpendicular to path direction
   */
  lateralOffset: { min: number; max: number };

  /**
   * Platform size range (randomized per platform)
   */
  platformSize: { min: Vector3; max: Vector3 };

  /**
   * Offset from path for platforms
   */
  pathOffset: Vector3;

  /**
   * Probability (0.0 - 1.0) that a platform will be a moving platform
   */
  movingPlatformChance: number;

  /**
   * Speed range for moving platforms
   */
  movingPlatformSpeed: { min: number; max: number };

  /**
   * Distance range for moving platform travel
   */
  movingPlatformDistance: { min: number; max: number };

  /**
   * Probability (0.0 - 1.0) that a platform will be a crumbling platform
   */
  crumblingPlatformChance: number;

  /**
   * Configuration for crumbling platforms
   */
  crumblingPlatformConfig: {
    triggerDelay: number;
    erosionDuration: number;
    fallSpeed: number;
    respawnTime: number;
  };

  /**
   * If true, difficulty (gap distance, height variance) increases along the path
   */
  difficultyProgression: boolean;

  /**
   * Difficulty multiplier at the end of the path (1.0 = no change, 2.0 = double)
   */
  difficultyMultiplier: number;

  // ========== Material Indices ==========

  /**
   * Material index or name for static platforms
   */
  platformMaterial: number | string;

  /**
   * Material index or name for moving platforms
   */
  movingPlatformMaterial: number | string;

  /**
   * Material index or name for crumbling platforms
   */
  crumblingPlatformMaterial: number | string;

  /**
   * Platform asset to use for platforms
   * If not set, simple geometries will be used
   */
  platformAsset?: PlatformAssetConfig;

  /**
   * Platform assets to use for crumbling platforms
   * If not set, simple geometries will be used
   */
  crumblingPlatformAsset?: PlatformAssetConfig;

  /**
   * Platform asset to use for moving platforms
   * If not set, simple geometries will be used
   */
  movingPlatformAsset?: PlatformAssetConfig;

  /**
   * If true, create a ground platform at the start
   */
  createStartGround?: boolean;

  /**
   * Size of the start ground platform
   */
  startGroundSize?: Vector3;

  /**
   * Platform asset to use for start ground
   * If not set, simple geometries will be used
   */
  startGroundAsset?: PlatformAssetConfig;

  /**
   * If true, create a ground platform at the destination
   */
  createDestinationGround?: boolean;

  /**
   * Size of the destination ground platform
   */
  destinationGroundSize?: Vector3;

  /**
   * Platform asset to use for destination ground
   * If not set, simple geometries will be used
   */
  destinationGroundAsset?: PlatformAssetConfig;
}

export interface NumberRange {
  min: number;
  max: number;
}

/**
 * Configuration for lights along the path
 */
export interface PathLightsConfig {
  /**
   * Light colors
   */
  colors: Color3[];

  /**
   * Light intensity
   */
  intensity: number;

  /**
   * If true, light is a spot light, pointing to the path
   * If false, light is a point light
   */
  spot: boolean;

  /**
   * Interval between lights
   */
  interval?: number;

  /**
   * Probability of a light at each interval
   */
  probability?: number;

  /**
   * Offset from path for lights
   */
  offset?: Vector3;

  /**
   * Spot lights direction
   */
  direction?: Vector3;

  /**
   * Spot light angle in radians
   */
  angle?: number;

  /**
   * Spot light exponent
   */
  exponent?: number;
}

export interface PathConfig {
  /**
   * Paths start position
   */
  start: Vector3;

  /**
   * Paths end position
   */
  destination: Vector3;

  /**
   * Number of points to generate for the path
   */
  pathPoints?: number;

  /**
   * Number of points between each curve control points
   */
  segmentsPerCurve?: number;

  /**
   * Path curvature: 0 = straight line, > 0 = more winding
   */
  pathCurvature?: number;

  /**
   * Path hilliness: 0 = flat, > 0 = more hilly
   */
  pathHilliness?: number;

  /**
   * Scale of the path features: 1 = normal, >1 = stretched/long curves, <1 = frantic/short curves
   */
  pathScale?: number;

  /**
   * Minimum angle between path segments in degrees (e.g. 60)
   * Helps preventing too sharp curves (which break extrusion)
   */
  pathMinCurveAngle?: number;

  /**
   * Width of one lane on the path
   */
  laneWidth: number;

  /**
   * Space around path
   * Levels dimensions are considered using this space around path
   */
  spaceAround: number;

  /**
   * Predefined path points
   */
  predefined?: Vector3[];

  /**
   * Path boundary is created with this width if > 0
   */
  pathBoundary?: number;
}

/**
 * Configuration for player
 */
export interface PlayerConfig {
  /**
   * Player start position index on the path
   */
  startPathIndex: number;

  /**
   * Player start position offset from path start position
   */
  startOffset: Vector3;

  /**
   * Player runs automatically with this speed factor, 0 = disabled, < 1 = slower, > 1 = faster
   */
  autoRun: number;

  /**
   * Player speed
   */
  speed: number;

  /**
   * Player jump speed
   */
  jumpSpeed: number;

  /**
   * Player backward speed
   */
  backwardSpeed: number;

  /**
   * Player turn speed
   */
  turnSpeed: number;

  /**
   * Player dash speed
   */
  dashSpeed: number;

  /**
   * Player projectile speed
   */
  projectileSpeed: number;
}

/**
 * Configuration for procedural way generation along a path
 */
export interface WayGenerationConfig {
  /**
   * Width of the way
   */
  width?: number;

  /**
   * Height of the way
   */
  height?: number;

  /**
   * Number of segments across the way
   */
  segmentsAcross?: number;

  /**
   * Shape of the way
   */
  shape?: Vector3[];

  /**
   * Y offset of the way
   */
  yOffset?: number;

  /**
   * Material index or name for the way
   */
  material?: number | string;

  /**
   * Material index or name for the side of the way
   */
  sideMaterial?: number | string;

  /**
   * Probability of a gap in the way
   */
  gapProbability?: number;

  /**
   * Size of the gaps in the way
   */
  gapSize?: NumberRange;

  /**
   * Probability of a hole in the way
   * If > 0, holes will be generated in the way segments.
   */
  holeProbability?: number;

  /**
   * Offset of the holes from the paths endpoints
   */
  holePathOffset?: number;

  /**
   * Size of the holes in the way segments.
   */
  holeSize?: NumberRange;

  /**
   * Angle range (radians) for hole rotation around the path tangent axis.
   * min=0 is bottom, max=2*PI is full rotation (holes on all sides).
   * Default: { min: 0, max: 2*PI }
   */
  holeAngle?: NumberRange;
}

export interface WaterConfig {
  /**
   * Water level
   */
  level: number;

  /**
   * Water depth
   */
  depth: number;

  /**
   * Water size
   */
  size: number;

  /**
   * Show water effect under the level
   */
  effect: boolean;

  /**
   * Generate water mesh
   */
  mesh: boolean;

  /**
   * Waters bump texture URL
   */
  bumpTextureUrl: string;

  /**
   * Waters render target size
   */
  renderTargetSize: Vector2;

  /**
   * Waters wind force
   */
  windForce: number;

  /**
   * Waters wave height
   */
  waveHeight: number;

  /**
   * Waters bump height
   */
  bumpHeight: number;

  /**
   * Waters wave length
   */
  waveLength: number;

  /**
   * Waters wind direction
   */
  windDirection: Vector2;

  /**
   * Waters color
   */
  waterColor: Color3;

  /**
   * Waters color blend factor
   */
  colorBlendFactor: number;

  /**
   * Waters alpha
   */
  alpha: number;

  /**
   * Waters bump scale
   */
  bumpScale: number;

  /**
   * Whether to enable back face culling for the water material.
   * Set to true when water is only seen from above (performance gain).
   * Default: true
   */
  backFaceCulling?: boolean;
}

export interface SoundConfig {
  backgroundMusic: string;
  backgroundMusicVolume: number;
}

export interface PatternConfig {
  preset?: PatternTypeConfig;
  positions?: Vector3[];
  lanes?: number[];
}

export interface PostProcessConfig {
  fxaa?: boolean;
  motionBlur?: number;
  blur?: { kernel: number; strength: number; x: number; y: number };
  highlights?: boolean;
  sepia?: boolean;
  rain?: number;
  heavyRain?: number;
  advancedRain?: number;
  fireflies?: number;
  snow?: number;
  birds?: number;
  groundFog?: number;
}

export interface CameraConfig {
  /**
   * Align camera to path
   */
  alignToPath: boolean;

  /**
   * Camera distance to player
   */
  distance: number;

  /**
   * Camera height offset to player
   */
  heightOffset: number;

  /**
   * Camera smoothing
   * Higher = faster camera following
   */
  smoothing: number;

  /**
   * Camera alpha smoothing
   * Lower = smoother/slower camera rotation
   */
  alphaSmoothing: number;

  /**
   * Enable camera collision
   */
  collision: boolean;

  /**
   * Orthographic camera
   */
  orthographic?: boolean;

  /**
   * Orthographic camera zoom
   */
  orthographicZoom?: number;

  /**
   * Orthographic camera alpha
   */
  orthographicAlpha?: number;

  /**
   * Orthographic camera beta
   */
  orthographicBeta?: number;

  /**
   * Camera horizontal offset to player (offset along movement direction)
   */
  orthographicHorizontalOffset?: number;

  /**
   * Camera vertical offset to player (offset perpendicular to movement direction)
   */
  orthographicVerticalOffset?: number;
}

export interface BaseSceneConfig {
  object: AssetObject;
  offset: Vector3;
  exclude: string[];
  disablePhysics: string[];
  disableGround: string[];
  disableShadowCasters: string[];
}

export interface TextureConfig {
  name?: string;
  url?: string;
  color?: string;
  shader?: string;
  uScale?: number;
  vScale?: number;
  uOffset?: number;
  vOffset?: number;
  bumpUrl?: string;
  alpha?: number;
  roughness?: number;
  metallic?: number;
}

export interface MaterialConfig {
  /**
   * Base color of the color materials
   */
  colorMaterialBaseColor?: Color3;

  /**
   * Number of color materials
   */
  colorMaterialCount?: number;

  /**
   * URLs of the texture materials to be used for this level
   */
  textures?: TextureConfig[];

  /**
   * Names of the global texture materials to be used for this level, must be created before
   */
  globalTextureNames?: string[];

  /**
   * Names of the materials to be used for this level
   */
  materials: string[];
}

export interface SpriteConfig {
  asset?: Asset;
  assetName?: string;
  name: string;
  cellWidth: number;
  cellHeight: number;
  capacity: number;
  scale: NumberRange;
  yRange: NumberRange;
  /**
   * Dead zone around path
   */
  deadZone?: NumberRange;
  color?: Color4;
  offsetY?: number;
  atlas?: boolean;
  image?: string;
}
